import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Table, Button } from "antd";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import Breadcrumbs from "../../../components/Breadcrumbs";
import SearchBox from "../../../components/SearchBox";
import LocationModelPopup from "../Locations/components/popup/LocationModelPopup";
import moment from "moment";

const Locations = () => {
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");
  const [loading, setLoading] = useState(true);
  const maximizeBtnRef = useRef(null);
  const [isFullScreen, setFullScreen] = useState(false);

  // Maximize screen functionality
  useEffect(() => {
    const handleClick = () => {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen();
        setFullScreen(true);
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen();
          setFullScreen(false);
        }
      }
    };

    const cleanup = () => {
      if (isFullScreen && document.exitFullscreen) {
        document.exitFullscreen();
        setFullScreen(false);
      }
    };

    const maximizeBtn = maximizeBtnRef.current;
    maximizeBtn.addEventListener("click", handleClick);

    return () => {
      maximizeBtn.removeEventListener("click", handleClick);
      cleanup();
    };
  }, [isFullScreen]);

  // Fetch locations
  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${apiUrl}/company/locations/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setLocations(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching locations:", error);
      toast.error("Failed to fetch locations. Please try again later.");
      setLoading(false);
    }
  };

  useEffect(() => {
    if (apiUrl && token) {
      fetchLocations();
    }
  }, [apiUrl, token]);

  // Periodic refresh
  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchLocations();
    }, 5000);

    return () => clearInterval(intervalId);
  }, []);

  // Handle Save
  const handleSave = (location) => {
    if (isEditing) {
      setLocations((prevLocations) =>
        prevLocations.map((loc) =>
          loc.id === location.id ? location : loc
        )
      );
    } else {
      setLocations((prevLocations) => [...prevLocations, location]);
    }

    setIsEditing(false);
    setSelectedLocation(null);
  };

  // Handle Edit
  const handleEditClick = (location) => {
    setSelectedLocation(location);
    setIsEditing(true);
  };

  // Handle Add
  const handleAddClick = () => {
    setIsEditing(false);
    setSelectedLocation(null);
  };

  // Handle Delete
  const handleDeleteClick = async (locationId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this location!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${apiUrl}/company/delete-locations/${locationId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setLocations((prevLocations) =>
          prevLocations.filter((loc) => loc.id !== locationId)
        );

        toast.success("Location has been deleted.");
      } catch (error) {
        let errorMessage = "Failed to delete location. Please try again later.";
        if (error.response?.data?.error) {
          errorMessage = error.response.data.error;
        }
        Swal.fire("Error!", errorMessage, "error");
      }
    }
  };

  // Toggle Location Active/Inactive Status
  const updateLocationStatus = async (locationId, currentStatus) => {
    const newStatus = currentStatus === 1 ? 0 : 1;
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `You are about to change the status to ${
        newStatus === 1 ? "Active" : "Inactive"
      }`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, change it!",
      cancelButtonText: "No, cancel!",
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      try {
        await axios.put(
          `${apiUrl}/company/edit-locations/${locationId}`,
          { status: newStatus },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        setLocations(
          locations.map((loc) =>
            loc.id === locationId
              ? { ...loc, isactive: newStatus }
              : loc
          )
        );

        await Swal.fire(
          "Updated!",
          "Location status has been updated.",
          "success"
        );
      } catch (error) {
        let errorMessage =
          "Failed to update location status. Please try again later.";
        if (error.response?.data?.error) {
          errorMessage = error.response.data.error;
        }
        await Swal.fire("Error!", errorMessage, "error");
      }
    }
  };

  // Table columns
  const columns = [
    {
      title: "Location Name",
      dataIndex: "location",
      key: "location",
      sorter: (a, b) => a.location.localeCompare(b.location),
    },
    {
      title: "#Employees",
      dataIndex: "employees",
      key: "employees",
      sorter: (a, b) => a.employees - b.employees,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sorter: (a, b) => a.email.localeCompare(b.email),
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      render: (_, record) =>
        `${record.address || ""}, ${record.city || ""}, ${
          record.state || ""
        } ${record.zip || ""}`.trim() || "N/A",
    },
   
    {
      title: "Status",
      dataIndex: "isactive",
      key: "isactive",
      render: (text, record) => {
        const isActive = text === 1;
        return (
          <div className="dropdown action-label">
            <Link
              className={`btn btn-white btn-sm btn-rounded dropdown-toggle ${
                isActive ? "text-success" : "text-danger"
              }`}
              to="#"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i
                className={`far fa-dot-circle ${
                  isActive ? "text-success" : "text-danger"
                }`}
              />{" "}
              {isActive ? "Active" : "Inactive"}
            </Link>
            <div className="dropdown-menu">
              <Link
                className="dropdown-item"
                to="#"
                onClick={() => updateLocationStatus(record.id, record.isactive)}
              >
                <i
                  className={`far fa-dot-circle ${
                    !isActive ? "text-success" : "text-danger"
                  }`}
                />{" "}
                {!isActive ? "Active" : "Inactive"}
              </Link>
            </div>
          </div>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <div className="dropdown dropdown-action text-center">
          <Link
            to="#"
            className="action-icon dropdown-toggle"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            <i className="material-icons">more_vert</i>
          </Link>
          <div className="dropdown-menu dropdown-menu-center">
            <Link
              onClick={() => handleEditClick(record)}
              className="dropdown-item"
              to="#"
              data-bs-toggle="modal"
              data-bs-target="#add_location"
            >
              <i className="fa fa-pencil m-r-5" /> Edit
            </Link>
            <button
              className="dropdown-item"
              onClick={() => handleDeleteClick(record.id)}
            >
              <i className="fa fa-trash m-r-5" /> Delete
            </button>
          </div>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="page-header page-top-icons">
            <div className="row align-items-center">
              <div className="col-md-4">
                <h3 className="page-title">Locations</h3>
              </div>
              <div className="col-md-8 float-end ms-auto">
                <div className="d-flex title-head">
                  <div className="view-icons">
                    <Link
                      to="#"
                      className="grid-view btn btn-link"
                      onClick={fetchLocations}
                    >
                      <i className="las la-redo-alt" />
                    </Link>
                    <Link
                      to="#"
                      className="list-view btn btn-link"
                      id="collapse-header"
                      ref={maximizeBtnRef}
                    >
                      <i className="las la-expand-arrows-alt" />
                    </Link>
                  </div>
                  <Link
                    to="#"
                    className="btn add-btn"
                    data-bs-toggle="modal"
                    data-bs-target="#add_location"
                    onClick={handleAddClick}
                  >
                    <i className="la la-plus-circle" /> Add New
                  </Link>
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-md-12">
              {loading ? (
                <div id="loader-wrapper-">
                  <div className="loader">
                    <div className="dot"></div>
                    <div className="dot"></div>
                    <div className="dot"></div>
                    <div className="dot"></div>
                    <div className="dot"></div>
                  </div>
                </div>
              ) : (
                <div className="table-responsive">
                  <SearchBox />
                  <Table
                    className="table-striped"
                    style={{ overflowX: "auto" }}
                    columns={columns}
                    dataSource={locations}
                    rowKey={(record) => record.id}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <LocationModelPopup
        onSave={handleSave}
        locationData={selectedLocation}
        isEditing={isEditing}
      />
    </>
  );
};

export default Locations;
